import { usePreferencesStore } from "@/stores/usePreferencesStore";
import { getLocaleForLanguage, type LanguageCode } from "@/preferences/types";

type CurrencyOptions = Intl.NumberFormatOptions & {
  currency?: string;
};

const getActiveLanguage = (language?: LanguageCode): LanguageCode =>
  language ?? usePreferencesStore.getState().language;

const getCurrentLocale = (language?: LanguageCode) =>
  getLocaleForLanguage(getActiveLanguage(language));

export const formatNumber = (
  value: number,
  language?: LanguageCode,
  options?: Intl.NumberFormatOptions,
) =>
  new Intl.NumberFormat(getCurrentLocale(language), options).format(Number(value || 0));

export const formatCurrency = (
  value: number,
  language?: LanguageCode,
  options?: CurrencyOptions,
) => {
  const { currency = "MXN", ...rest } = options ?? {};

  return new Intl.NumberFormat(getCurrentLocale(language), {
    style: "currency",
    currency,
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
    ...rest,
  }).format(Number(value || 0));
};

export const formatSignedCurrency = (
  value: number,
  language?: LanguageCode,
  options?: CurrencyOptions,
) => {
  const absolute = formatCurrency(Math.abs(value), language, options);
  return value < 0 ? `-${absolute}` : value > 0 ? `+${absolute}` : absolute;
};

export const formatPercent = (
  value: number,
  language?: LanguageCode,
  options?: Intl.NumberFormatOptions,
) =>
  formatNumber(value, language, {
    minimumFractionDigits: 1,
    maximumFractionDigits: 1,
    ...options,
  });

export const formatDate = (
  value: Date | string,
  language?: LanguageCode,
  options?: Intl.DateTimeFormatOptions,
) => {
  const date = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(date.getTime())) return "";

  return new Intl.DateTimeFormat(getCurrentLocale(language), options).format(date);
};

export type AmountFlow = "income" | "expense" | "transfer";

const FLOW_ARROW: Record<AmountFlow, string> = { income: "↑", expense: "↓", transfer: "↔" };

/** Flecha de un flujo: "↑" ingreso, "↓" gasto, "↔" transferencia. */
export const flowArrow = (flow: AmountFlow) => FLOW_ARROW[flow];

interface FlowAmountOptions {
  /**
   * "flow": "+" en ingreso y "-" en gasto (default).
   * "expense": solo "-" en gasto, el ingreso va sin signo.
   * "none": sin signo.
   */
  sign?: "flow" | "expense" | "none";
  /** Sin signo cuando el monto es 0, igual que `formatSignedCurrency`. */
  omitZeroSign?: boolean;
}

/** Monto absoluto con flecha y signo del flujo: "↓ -$180.00", "↑ +$1,200.00". */
export const formatFlowAmount = (amount: number, flow: AmountFlow, options: FlowAmountOptions = {}) => {
  const { sign = "flow", omitZeroSign = false } = options;
  const absolute = Math.abs(amount);
  const showSign = !(omitZeroSign && absolute === 0) && sign !== "none";
  const mark =
    !showSign ? "" : flow === "expense" ? "-" : flow === "income" && sign === "flow" ? "+" : "";

  return `${flowArrow(flow)} ${mark}${formatCurrency(absolute)}`;
};

interface NetAmountOptions {
  /** Antepone "+" a los valores positivos (default true). */
  plus?: boolean;
}

/**
 * Valor neto con flecha según su signo ("↑ +$1,200.00", "↓ -$500.00", "$0.00").
 * Sin flecha cuando el valor es 0.
 */
export const formatNetAmount = (value: number, options: NetAmountOptions = {}) => {
  const { plus = true } = options;
  if (value === 0) return formatCurrency(0);

  const mark = value < 0 ? "-" : plus ? "+" : "";
  const amount = `${mark}${formatCurrency(Math.abs(value))}`;
  return `${flowArrow(value > 0 ? "income" : "expense")} ${amount}`;
};
